import { useMemo } from 'react';
import { useTenant } from './useTenant';
import { useTimezone } from '@/contexts/TimezoneContext';
import {
  resolveCompanyLocale,
  formatMoney as formatMoneyRaw,
  formatDocDate as formatDocDateRaw,
  type CompanyLocale,
} from '@/utils/locale';

export interface CompanyLocaleResult {
  locale: CompanyLocale;
  timezone: string;
  /** Currency formatted for the tenant's market ("$1,234.50", "£1,234.50"). */
  formatMoney: (amount: number | null | undefined) => string;
  /** Long document date ("8 September 2026"), timezone- and date-only-safe. */
  formatLongDate: (value: string | Date | null | undefined) => string;
  /** Compact table date ("8 Sep 2026"). */
  formatShortDate: (value: string | Date | null | undefined) => string;
}

/**
 * Locale-aware formatting for the current tenant. Derived from the company's
 * `country`; falls back to Australian defaults before the profile has loaded so
 * callers never crash on first render.
 */
export function useCompanyLocale(): CompanyLocaleResult {
  const { company } = useTenant();
  const { timezone } = useTimezone();
  const country = company?.country;

  return useMemo<CompanyLocaleResult>(() => {
    const locale = resolveCompanyLocale(country);
    return {
      locale,
      timezone,
      formatMoney: (amount) => formatMoneyRaw(amount, locale),
      formatLongDate: (value) => formatDocDateRaw(value, locale.longDate, timezone),
      formatShortDate: (value) => formatDocDateRaw(value, locale.shortDate, timezone),
    };
  }, [country, timezone]);
}
